<script setup lang="ts">

import { ref, watch } from 'vue';
import MoneyTimePlotBase from './MoneyTimePlotBase.vue';
import { ColoredSeries, TimeSeries, TimeSeriesS } from '../fin/history';

interface Props {
  timeseriess: TimeSeriesS<number>;
  birthYear: number;
  retirementYear: number;
  maxYear: number;
}

// "Reactive Props Destructure" to initialise props:
const {
  timeseriess = new TimeSeriesS<number>([]),
  birthYear = 0,
  retirementYear = 0,
  maxYear = 0,
} = defineProps<Props>();

const coloredSeriess = ref(getColoredSeries());

watch( ()=>timeseriess, (_newVal, _oldVal ) => {
  coloredSeriess.value = getColoredSeries();
});

function getColoredSeries(): Array<ColoredSeries<number>> {
  function extractSeries(name: string): TimeSeries<number> {
    return timeseriess.data.find(h => h.name == name) ?? new TimeSeries<number>('unknown', []);
  }

  if (timeseriess.data.length == 0) {
    return [];
  } 

  const pension = extractSeries('PENSION');
  const unprotected = extractSeries('UNPROTECTED');
  const isa = extractSeries('ISA');
  const tax_free_pension_allowance = extractSeries('TAX_FREE_PENSION');

  const cseriess = [
    {
      name: 'isa savings pool',
      color: 'rgb(50,50,200)',
      data: isa,
    }, {
      name: 'defined contribution pension savings pool',
      color: 'sienna',
      data: pension,
    }, {
      name: 'unprotected assets savings pool',
      color: 'red',
      data: unprotected,
    }, {
      name: 'tax free pension allowance',
      color: 'gray',
      data: tax_free_pension_allowance,
    },
  ].map(s => new ColoredSeries<number>(s.name, s.data.data, s.color));

  return cseriess;
}

function drawLines(
  svg: d3.Selection<SVGSVGElement, undefined, null, undefined>,
  x: (x:number)=>number,
  y: (y:number)=>number,
): void {
  const frameWidthString = svg.node()!.getAttribute('width');
  const frameWidth = +(frameWidthString ?? '1000');

  const yearCount = new Set<number>(
    coloredSeriess.value.map(cs => cs.data.map(d => d.year)).flat()
  ).size;

  const seriesCount = coloredSeriess.value.length;
  const barCount = yearCount*seriesCount;
  const barWidth = Math.max(Math.round(0.7 * frameWidth/barCount), 2);
  const groupOffset = Math.round(barWidth*seriesCount/2);

  for (const [index, series] of Array.from(coloredSeriess.value.entries())) {
    svg.append('g')
       .classed('fin-series', true)
       .attr('fill', series.color)
       .selectAll()
       .data(series.data)
       .join('rect')
       .attr('x', d => x(d.year) + index*barWidth - groupOffset)
       .attr('width', 5)
       .attr('y', d => y(d.value))
       .attr('height', d => y(0) - y(d.value))
       .append('title')
       .text(d => series.name + ' '
                              + d.year
                              + ' £'
                              + Math.round(d.value).toLocaleString());
  };
}

</script>

<template>
  <MoneyTimePlotBase
    id="fin-plot:pool-value"
    title="Pools"
    :maxYear="maxYear"
    :birthYear="birthYear"
    :retirementYear="retirementYear"
    :seriess="coloredSeriess" 
    :seriesRenderer="drawLines"
  />
</template>

<style scoped>
</style>
